import * as Primitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import {
  Fragment,
  forwardRef,
  useCallback,
  useId,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { Button } from './button';
import './select.css';

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
  group?: string;
}
export type SelectProps = Omit<ComponentPropsWithoutRef<typeof Primitive.Root>, 'children'> &
  Pick<
    ComponentPropsWithoutRef<typeof Primitive.Trigger>,
    'id' | 'aria-label' | 'aria-labelledby' | 'aria-describedby' | 'aria-invalid'
  > & {
    options: readonly SelectOption[];
    placeholder?: string;
    className?: string;
    triggerContent?: ReactNode;
    popupClassName?: string;
  };

/** Shared, styled select with managed focus, keyboard search and native form values. */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    placeholder,
    className = '',
    triggerContent,
    popupClassName = '',
    id,
    onOpenChange,
    'aria-label': label,
    'aria-labelledby': labelledBy,
    'aria-describedby': describedBy,
    'aria-invalid': invalid,
    ...rootProps
  },
  forwardedRef,
) {
  const trigger = useRef<HTMLButtonElement | null>(null);
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const [container, setContainer] = useState<HTMLElement | undefined>();
  const ref = useCallback(
    (node: HTMLButtonElement | null) => {
      trigger.current = node;
      if (node) setContainer(node.closest('dialog') ?? undefined);
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );
  const groups = [...new Set(options.map((option) => option.group ?? ''))];
  return (
    <Primitive.Root
      {...rootProps}
      onOpenChange={(open) => {
        if (open) setContainer(trigger.current?.closest('dialog') ?? undefined);
        onOpenChange?.(open);
      }}
    >
      <Primitive.Trigger
        asChild
        aria-label={label}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-invalid={invalid}
      >
        <Button
          ref={ref}
          id={triggerId}
          variant="bare"
          className={`ui-input ui-select-trigger ${className}`.trim()}
        >
          <Primitive.Value placeholder={placeholder}>{triggerContent}</Primitive.Value>
          <Primitive.Icon asChild>
            <ChevronDown className="ui-select-chevron" size={17} aria-hidden="true" />
          </Primitive.Icon>
        </Button>
      </Primitive.Trigger>
      <Primitive.Portal container={container}>
        <Primitive.Content
          className={`ui-select-popup ${popupClassName}`.trim()}
          position="popper"
          sideOffset={7}
          collisionPadding={10}
          aria-label={label}
          aria-labelledby={labelledBy ?? (label ? undefined : triggerId)}
        >
          <Primitive.ScrollUpButton className="ui-select-scroll">
            <ChevronUp size={16} aria-hidden="true" />
          </Primitive.ScrollUpButton>
          <Primitive.Viewport className="ui-select-viewport">
            {groups.map((group, index) => {
              const items = options
                .filter((option) => (option.group ?? '') === group)
                .map((option) => (
                  <Primitive.Item
                    className="ui-select-option"
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    textValue={option.label}
                  >
                    <Primitive.ItemText>
                      {option.icon ? (
                        <span className="ui-select-option-label">
                          <span aria-hidden="true">{option.icon}</span>
                          {option.label}
                        </span>
                      ) : (
                        option.label
                      )}
                    </Primitive.ItemText>
                    <Primitive.ItemIndicator className="ui-select-check">
                      <Check size={17} aria-hidden="true" />
                    </Primitive.ItemIndicator>
                  </Primitive.Item>
                ));
              return (
                <Fragment key={group}>
                  {index > 0 && <Primitive.Separator className="ui-select-separator" />}
                  {group ? (
                    <Primitive.Group>
                      <Primitive.Label className="ui-select-group-label">{group}</Primitive.Label>
                      {items}
                    </Primitive.Group>
                  ) : (
                    items
                  )}
                </Fragment>
              );
            })}
          </Primitive.Viewport>
          <Primitive.ScrollDownButton className="ui-select-scroll">
            <ChevronDown size={16} aria-hidden="true" />
          </Primitive.ScrollDownButton>
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
});
